import { useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import {
  Box,
  Stack,
  Typography,
  Card,
  Button,
  Chip,
  TextField,
  InputAdornment,
  LinearProgress,
  Tooltip,
} from "@mui/material";
import {
  Bell,
  Search,
  CheckCheck,
  Settings,
  Inbox,
  Mail,
  OctagonAlert,
  TriangleAlert,
  Wifi,
  WifiOff,
  MapPin,
  Ban,
  Play,
  Activity,
  BatteryLow,
  BatteryMedium,
  Power,
  Bot,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  X,
} from "lucide-react";
import useNotifications from "./useNotifications";
import useRobotFilterOptions from "./useRobotFilterOptions";
import useNotificationsSocket from "../../hooks/useNotificationsSocket";
import { useNotificationPrefs } from "../../hooks/useNotificationPrefs";
import { isNotificationEnabled } from "../../utils/notificationPrefs";
import { tokens } from "../../theme";
import { pageContainerSx } from "../../components/ui";

/** "2026-08-13 14:32:07" — unambiguous, and what you want when correlating with a log. */
const formatTimestamp = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
};

const relativeTime = (iso) => {
  if (!iso) return "";
  const diffSec = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (!Number.isFinite(diffSec) || diffSec < 0) return "";
  if (diffSec < 60) return `${diffSec}s ago`;
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`;
  return `${Math.floor(diffSec / 86400)}d ago`;
};

/** type → how the row is drawn. The admin feed's union, restricted to events a
 * customer can actually receive — admin-only rows never reach the portal
 * endpoint, so they are omitted rather than dead-mapped. */
const TYPE_META = {
  destination_reached: { label: "Arrived", icon: MapPin, sev: "success" },
  navigation_failed: { label: "Nav failed", icon: TriangleAlert, sev: "danger" },
  navigation_cancelled: { label: "Nav cancelled", icon: Ban, sev: "warning" },
  navigation_resumed: { label: "Resumed", icon: Play, sev: "accent" },
  emergency_stop: { label: "E-stop", icon: OctagonAlert, sev: "danger" },
  robot_connected: { label: "Connected", icon: Wifi, sev: "success" },
  robot_disconnected: { label: "Disconnected", icon: WifiOff, sev: "warning" },
  sensor_health: { label: "Sensor", icon: Activity, sev: "warning" },
  low_battery: { label: "Battery", icon: BatteryLow, sev: "warning" },
  startup_ready: { label: "Startup", icon: Power, sev: "success" },
};

const FALLBACK_META = { label: "Event", icon: Bell, sev: "accent" };

const SEVERITY_TONE = {
  info: { color: tokens.accent, soft: tokens.accentSoft },
  success: { color: tokens.success, soft: tokens.successSoft },
  warning: { color: tokens.warning, soft: tokens.warningSoft },
  critical: { color: tokens.danger, soft: tokens.dangerSoft },
  // TYPE_META's own tones (success/warning/danger/accent) reuse the same table —
  // "danger" is the type-tone spelling of the severity enum's "critical".
  danger: { color: tokens.danger, soft: tokens.dangerSoft },
  accent: { color: tokens.accent, soft: tokens.accentSoft },
};

const toneOf = (key) => SEVERITY_TONE[key] || SEVERITY_TONE.accent;

const SEVERITY_OPTIONS = [
  { value: "", label: "All severities" },
  { value: "critical", label: "Critical" },
  { value: "warning", label: "Warning" },
  { value: "success", label: "OK" },
  { value: "info", label: "Info" },
];

const TYPE_OPTIONS = [
  { value: "", label: "All types" },
  ...Object.entries(TYPE_META).map(([value, meta]) => ({ value, label: meta.label })),
];

const READ_OPTIONS = [
  { value: "", label: "All notifications" },
  { value: "true", label: "Unread only" },
];

const selSx = {
  width: "100%",
  "& .MuiOutlinedInput-root": {
    height: 34,
    borderRadius: "8px",
    fontSize: 13,
    background: tokens.surface,
  },
  "& fieldset": { borderColor: tokens.border },
  "&:hover fieldset": { borderColor: tokens.borderStrong },
  "& .Mui-focused fieldset": { borderColor: tokens.accent },
};

const labelSx = { fontSize: 11.5, fontWeight: 600, color: tokens.text3, mb: 0.4 };

/* ── Stat tile ─────────────────────────────────────────────────────────────── */
const StatTile = ({ label, value, tone, Icon }) => (
  <Card sx={{ flex: 1, minWidth: 120, p: 1.75 }}>
    <Stack direction="row" alignItems="center" gap={1} mb={0.75}>
      <Icon size={14} color={tone ? toneOf(tone).color : tokens.text3} />
      <Typography
        sx={{
          fontSize: 11,
          fontWeight: 600,
          color: tokens.text3,
          textTransform: "uppercase",
          letterSpacing: ".04em",
        }}
      >
        {label}
      </Typography>
    </Stack>
    <Typography sx={{ fontSize: 22, fontWeight: 700, color: tokens.text, lineHeight: 1 }}>
      {value ?? 0}
    </Typography>
  </Card>
);

StatTile.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.number,
  tone: PropTypes.string,
  Icon: PropTypes.elementType.isRequired,
};

/* ── One notification row ──────────────────────────────────────────────────── */
const NotificationRow = ({ notification, first, onMarkRead }) => {
  const meta = TYPE_META[notification.type] || FALLBACK_META;
  const tone = toneOf(notification.severity || meta.sev);
  const unread = !notification.isRead;
  const { metadata } = notification;
  const TypeIcon = meta.icon;

  return (
    <Stack
      direction="row"
      alignItems="flex-start"
      gap={1.5}
      onClick={() => unread && onMarkRead(notification._id)}
      sx={{
        p: 1.75,
        borderTop: first ? "none" : `1px solid ${tokens.border}`,
        cursor: unread ? "pointer" : "default",
        // Unread rows carry a left accent bar and a faint tint — a dot alone is
        // easy to miss in a long list; the whole row reading as "new" is not.
        borderLeft: unread ? `3px solid ${tone.color}` : "3px solid transparent",
        background: unread ? tone.soft : "transparent",
        transition: "background .15s ease",
      }}
    >
      <Box
        sx={{
          width: 34,
          height: 34,
          borderRadius: "9px",
          flex: "none",
          display: "grid",
          placeItems: "center",
          background: tone.soft,
          color: tone.color,
        }}
      >
        <TypeIcon size={17} />
      </Box>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Stack direction="row" alignItems="center" gap={0.75} flexWrap="wrap">
          <Typography
            sx={{
              fontSize: 13,
              fontWeight: unread ? 700 : 600,
              color: tokens.text,
              wordBreak: "break-word",
            }}
          >
            {notification.title}
          </Typography>
          <Chip
            label={meta.label}
            size="small"
            sx={{
              height: 18,
              fontSize: 10.5,
              fontWeight: 600,
              color: tone.color,
              background: tone.soft,
            }}
          />
          {unread && (
            <Chip
              label="New"
              size="small"
              sx={{
                height: 18,
                fontSize: 10.5,
                fontWeight: 600,
                color: tokens.accent,
                background: tokens.accentSoft,
              }}
            />
          )}
        </Stack>

        <Typography sx={{ fontSize: 12.5, color: tokens.text2, mt: 0.4, wordBreak: "break-word" }}>
          {notification.message}
        </Typography>

        {/* Event detail worth reading at a glance — the destination that was
            reached, the sensor that changed, the battery level. Rendered only
            when present, so account-level rows (which have none) stay compact. */}
        <Stack direction="row" alignItems="center" gap={1.25} flexWrap="wrap" sx={{ mt: 0.6 }}>
          {notification.serialID && (
            <Stack direction="row" alignItems="center" gap={0.5}>
              <Bot size={12} color={tokens.text3} />
              <Typography sx={{ fontSize: 11, color: tokens.text3 }}>
                {notification.serialID}
              </Typography>
            </Stack>
          )}
          {metadata?.destination && (
            <Stack direction="row" alignItems="center" gap={0.5}>
              <MapPin size={12} color={tokens.text3} />
              <Typography sx={{ fontSize: 11, color: tokens.text3 }}>
                {metadata.destination}
              </Typography>
            </Stack>
          )}
          {metadata?.sensorLabel && (
            <Stack direction="row" alignItems="center" gap={0.5}>
              <Activity size={12} color={tokens.text3} />
              <Typography sx={{ fontSize: 11, color: tokens.text3 }}>
                {metadata.sensorLabel}
                {metadata.previousCriticality
                  ? ` · ${metadata.previousCriticality} → ${metadata.criticality}`
                  : ` · ${metadata.criticality}`}
              </Typography>
            </Stack>
          )}
          {typeof metadata?.batteryLevel === "number" && (
            <Stack direction="row" alignItems="center" gap={0.5}>
              <BatteryMedium size={12} color={tokens.text3} />
              <Typography sx={{ fontSize: 11, color: tokens.text3 }}>
                {Math.round(metadata.batteryLevel * 100) / 100}%
              </Typography>
            </Stack>
          )}
        </Stack>
      </Box>

      {/* Narrower on mobile: the full timestamp only shows at `md`+, so xs/sm
          need room for "23s ago" and not much else. */}
      <Stack sx={{ width: { xs: 56, sm: 150 }, flex: "none" }} alignItems="flex-end" gap={0.5}>
        <Tooltip title={formatTimestamp(notification.createdAt)} arrow>
          <Typography sx={{ fontSize: 11.5, color: tokens.text2, cursor: "default" }}>
            {relativeTime(notification.createdAt)}
          </Typography>
        </Tooltip>
        <Typography
          sx={{ fontSize: 11, color: tokens.text3, display: { xs: "none", md: "block" } }}
        >
          {formatTimestamp(notification.createdAt)}
        </Typography>
      </Stack>
    </Stack>
  );
};

NotificationRow.propTypes = {
  notification: PropTypes.shape({
    _id: PropTypes.string.isRequired,
    type: PropTypes.string,
    severity: PropTypes.string,
    isRead: PropTypes.bool,
    title: PropTypes.string,
    message: PropTypes.string,
    serialID: PropTypes.string,
    createdAt: PropTypes.string,
    metadata: PropTypes.shape({
      destination: PropTypes.string,
      sensorLabel: PropTypes.string,
      previousCriticality: PropTypes.string,
      criticality: PropTypes.string,
      batteryLevel: PropTypes.number,
    }),
  }).isRequired,
  first: PropTypes.bool,
  onMarkRead: PropTypes.func.isRequired,
};

const matchCountLabel = (total) => {
  if (total <= 0) return "No matching notifications";
  if (total === 1) return "1 matching notification";
  return `${total} matching notifications`;
};

const emptyStateMessage = (hasItems, hasActiveFilters) => {
  if (hasItems)
    return "Every notification on this page is hidden by your notification preferences.";
  if (hasActiveFilters) return "No notifications match these filters.";
  return "Nothing yet. Deliveries, maps and robot alerts appear here the moment they happen.";
};

const Notifications = () => {
  const navigate = useNavigate();

  const {
    items,
    stats,
    filters,
    setFilter,
    resetFilters,
    hasActiveFilters,
    page,
    setPage,
    total,
    totalPages,
    unreadCount,
    loading,
    error,
    markRead,
    markAllRead,
    refresh,
  } = useNotifications();

  const { botTypeOptions, robotOptions } = useRobotFilterOptions();

  // Refetch as soon as the backend signals that a new notification may exist,
  // instead of waiting for the next time the page is opened.
  useNotificationsSocket(refresh);

  const { prefs } = useNotificationPrefs();

  // Gated client-side on top of the server's filters: category preferences are a
  // portal-only setting the backend does not know about.
  const visibleItems = items.filter((n) => isNotificationEnabled(prefs, n.type));
  // The server-derived counts above know nothing about that gate, so a muted
  // category otherwise reads as a broken feed: real counts, empty list.
  const hiddenByPrefsCount = items.length - visibleItems.length;

  return (
    <Box sx={pageContainerSx()}>
      <Stack gap={2}>
        {/* Column on mobile, row on tablet+ — title plus a 2-3 button group
            wraps badly in one narrow row. Matches the admin feed's PageHead. */}
        <Stack
          direction={{ xs: "column", sm: "row" }}
          alignItems={{ xs: "stretch", sm: "center" }}
          justifyContent="space-between"
          gap={1.5}
        >
          <Stack direction="row" alignItems="center" gap={1.25} sx={{ minWidth: 0 }}>
            <Box
              sx={{
                width: 34,
                height: 34,
                borderRadius: "9px",
                background: tokens.accentSoft,
                color: tokens.accent,
                display: "grid",
                placeItems: "center",
                flexShrink: 0,
              }}
            >
              <Bell size={17} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h1">Notifications</Typography>
              <Typography sx={{ fontSize: 13, color: tokens.text2, mt: 0.2 }}>
                {unreadCount > 0
                  ? `${unreadCount} unread · deliveries, maps and robot alerts`
                  : "Deliveries, maps and robot alerts"}
              </Typography>
            </Box>
          </Stack>
          <Stack
            direction="row"
            alignItems="center"
            gap={1}
            flexWrap="wrap"
            justifyContent={{ xs: "flex-start", sm: "flex-end" }}
          >
            {unreadCount > 0 && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<CheckCheck size={15} />}
                onClick={markAllRead}
                sx={{ borderColor: tokens.border, color: tokens.text }}
              >
                Mark all read
              </Button>
            )}
            <Button
              variant="outlined"
              size="small"
              startIcon={<RefreshCw size={15} />}
              onClick={refresh}
              sx={{ borderColor: tokens.border, color: tokens.text }}
            >
              Refresh
            </Button>
            <Button
              variant="contained"
              size="small"
              startIcon={<Settings size={15} />}
              onClick={() => navigate("/settings")}
            >
              Preferences
            </Button>
          </Stack>
        </Stack>

        {/* ── Stat tiles ── */}
        <Stack direction="row" gap={1.5} flexWrap="wrap">
          <StatTile label="Total" value={stats.total} Icon={Inbox} />
          <StatTile label="Unread" value={stats.unread} tone="accent" Icon={Mail} />
          <StatTile label="Critical" value={stats.critical} tone="critical" Icon={OctagonAlert} />
          <StatTile label="Warning" value={stats.warning} tone="warning" Icon={TriangleAlert} />
        </Stack>

        {/* ── Filters + list ── */}
        <Card>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            flexWrap="wrap"
            gap={1}
            sx={{ p: 1.75, borderBottom: `1px solid ${tokens.border}` }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography
                sx={{
                  fontSize: 14,
                  fontWeight: 700,
                  color: tokens.text,
                  whiteSpace: "nowrap",
                }}
              >
                Event feed
              </Typography>
              <Typography
                sx={{
                  fontSize: 12,
                  color: tokens.text3,
                  mt: 0.2,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {matchCountLabel(total)}
              </Typography>
            </Box>
            <TextField
              size="small"
              placeholder="Search title or message…"
              value={filters.q}
              onChange={(e) => setFilter("q", e.target.value)}
              sx={{
                width: { xs: 220, sm: 300, md: 360 },
                "& .MuiOutlinedInput-root": { height: 34, borderRadius: "8px" },
                "& .MuiOutlinedInput-input": { py: 0, fontSize: 13 },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search size={15} color={tokens.text3} />
                  </InputAdornment>
                ),
              }}
            />
          </Stack>

          {/* FILTER ROW — matches the admin feed's grid so the two pages line up
              visually; scope/serialID are omitted since a customer's feed is
              already scoped to their own robots server-side. */}
          <Box sx={{ p: 1.75, borderBottom: `1px solid ${tokens.border}` }}>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(168px, 1fr))",
                gap: 1.25,
                alignItems: "start",
              }}
            >
              <Box>
                <Typography sx={labelSx}>Type</Typography>
                <TextField
                  select
                  size="small"
                  SelectProps={{ native: true }}
                  value={filters.type}
                  onChange={(e) => setFilter("type", e.target.value)}
                  sx={selSx}
                >
                  {TYPE_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </TextField>
              </Box>
              <Box>
                <Typography sx={labelSx}>Severity</Typography>
                <TextField
                  select
                  size="small"
                  SelectProps={{ native: true }}
                  value={filters.severity}
                  onChange={(e) => setFilter("severity", e.target.value)}
                  sx={selSx}
                >
                  {SEVERITY_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </TextField>
              </Box>
              <Box>
                <Typography sx={labelSx}>Bot type</Typography>
                <TextField
                  select
                  size="small"
                  SelectProps={{ native: true }}
                  value={filters.roverType}
                  onChange={(e) => setFilter("roverType", e.target.value)}
                  sx={selSx}
                >
                  {botTypeOptions.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </TextField>
              </Box>
              <Box>
                <Typography sx={labelSx}>Robot</Typography>
                <TextField
                  select
                  size="small"
                  SelectProps={{ native: true }}
                  value={filters.serialID}
                  onChange={(e) => setFilter("serialID", e.target.value)}
                  sx={selSx}
                >
                  {robotOptions.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </TextField>
              </Box>
              <Box>
                <Typography sx={labelSx}>Read state</Typography>
                <TextField
                  select
                  size="small"
                  SelectProps={{ native: true }}
                  value={filters.unreadOnly}
                  onChange={(e) => setFilter("unreadOnly", e.target.value)}
                  sx={selSx}
                >
                  {READ_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </TextField>
              </Box>
              <Box>
                <Typography sx={labelSx}>From</Typography>
                <TextField
                  size="small"
                  type="date"
                  value={filters.from}
                  onChange={(e) => setFilter("from", e.target.value)}
                  inputProps={filters.to ? { max: filters.to } : undefined}
                  sx={selSx}
                />
              </Box>
              <Box>
                <Typography sx={labelSx}>To</Typography>
                <TextField
                  size="small"
                  type="date"
                  value={filters.to}
                  onChange={(e) => setFilter("to", e.target.value)}
                  inputProps={filters.from ? { min: filters.from } : undefined}
                  sx={selSx}
                />
              </Box>
            </Box>

            {hasActiveFilters && (
              <Box sx={{ mt: 1.25, display: "flex", justifyContent: "flex-end" }}>
                <Button
                  size="small"
                  startIcon={<X size={14} />}
                  onClick={resetFilters}
                  sx={{ color: tokens.text2, textTransform: "none" }}
                >
                  Clear filters
                </Button>
              </Box>
            )}
          </Box>

          {/* Preference-gated rows on this page — shown whenever any exist, not
              just when they empty the whole page, so the count above and the
              rows below never silently disagree. */}
          {!loading && !error && hiddenByPrefsCount > 0 && (
            <Box
              sx={{
                px: 1.75,
                py: 1,
                borderBottom: `1px solid ${tokens.border}`,
                background: tokens.warningSoft,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexWrap: "wrap",
                gap: 1,
              }}
            >
              <Typography sx={{ fontSize: 12.5, color: tokens.text2 }}>
                {hiddenByPrefsCount} notification{hiddenByPrefsCount === 1 ? "" : "s"} on this page{" "}
                hidden by your notification preferences.
              </Typography>
              <Button
                size="small"
                onClick={() => navigate("/settings")}
                sx={{ color: tokens.accent, textTransform: "none", fontSize: 12.5 }}
              >
                Manage preferences
              </Button>
            </Box>
          )}

          {/* body */}
          {loading && (
            <LinearProgress
              sx={{
                borderRadius: 0,
                backgroundColor: tokens.surface3,
                "& .MuiLinearProgress-bar": { background: tokens.accent },
              }}
            />
          )}

          {!loading && error && (
            <Box sx={{ p: 3, textAlign: "center" }}>
              <TriangleAlert size={24} color={tokens.danger} />
              <Typography sx={{ fontSize: 13, color: tokens.danger, mt: 1 }}>{error}</Typography>
              <Box sx={{ mt: 1.5 }}>
                <Button
                  size="small"
                  startIcon={<RefreshCw size={14} />}
                  onClick={refresh}
                  sx={{ color: tokens.text2, textTransform: "none" }}
                >
                  Retry
                </Button>
              </Box>
            </Box>
          )}

          {!loading && !error && visibleItems.length === 0 && (
            <Box sx={{ p: 4, textAlign: "center" }}>
              <Bell size={26} color={tokens.text3} style={{ opacity: 0.6 }} />
              <Typography sx={{ fontSize: 13, color: tokens.text2, mt: 1 }}>
                {emptyStateMessage(items.length > 0, hasActiveFilters)}
              </Typography>
            </Box>
          )}

          {!loading &&
            !error &&
            visibleItems.map((n, i) => (
              <NotificationRow key={n._id} notification={n} first={i === 0} onMarkRead={markRead} />
            ))}

          {!loading && !error && visibleItems.length > 0 && (
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ p: 1.5, borderTop: `1px solid ${tokens.border}` }}
            >
              <Typography sx={{ fontSize: 12, color: tokens.text3 }}>
                Page {page} of {totalPages}
              </Typography>
              <Stack direction="row" gap={0.75}>
                <Button
                  size="small"
                  disabled={page <= 1}
                  onClick={() => setPage(page - 1)}
                  startIcon={<ChevronLeft size={14} />}
                  sx={{ color: tokens.text2, textTransform: "none" }}
                >
                  Prev
                </Button>
                <Button
                  size="small"
                  disabled={page >= totalPages}
                  onClick={() => setPage(page + 1)}
                  endIcon={<ChevronRight size={14} />}
                  sx={{ color: tokens.text2, textTransform: "none" }}
                >
                  Next
                </Button>
              </Stack>
            </Stack>
          )}
        </Card>
      </Stack>
    </Box>
  );
};

export default Notifications;
